import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Check, X, Loader2, AlertCircle, Phone, Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { publicBookingAPI } from '@/lib/api';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
        style: 'currency',
        currency: 'GHS'
    }).format(amount || 0);
};

const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return dateString;
        }
        return format(date, 'EEEE, MMMM d, yyyy');
    } catch (error) {
        return dateString;
    }
};

// Helper to extract error message from various error formats
const getErrorMessage = (err) => {
    if (!err) return 'An unknown error occurred';
    
    // If it's already a string, return it
    if (typeof err === 'string') return err;
    
    // If it's an object with a message property
    if (err.message && typeof err.message === 'string') return err.message;
    
    // If it's an axios error response
    if (err.response?.data) {
        const data = err.response.data;
        
        // Handle { error: "string" }
        if (typeof data.error === 'string') return data.error;
        
        // Handle { error: { code, message } }
        if (data.error?.message && typeof data.error.message === 'string') {
            return data.error.message;
        }
        
        // Handle { message: "string" }
        if (typeof data.message === 'string') return data.message;
        
        // Handle { detail: "string" }
        if (typeof data.detail === 'string') return data.detail;
    }
    
    return 'Failed to verify payment. Please contact the salon.';
};

export default function BookingPaymentCallback() {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('verifying');
    const [appointment, setAppointment] = useState(null);
    const [error, setError] = useState('');
    const [salonInfo, setSalonInfo] = useState(null);

    useEffect(() => {
        verifyPayment();
    }, []);

    const verifyPayment = async () => {
        try {
            // Try to get reference from URL first, then sessionStorage
            const urlReference = searchParams.get('reference');
            const storedReference = sessionStorage.getItem('booking_reference');
            const reference = urlReference || storedReference;
            const salonId = sessionStorage.getItem('booking_salon_id');
            
            console.log('Verifying payment with reference:', reference);
            
            if (!reference) {
                setStatus('failed');
                setError('Payment reference not found. Please contact the salon.');
                return;
            }

            // Call backend to verify payment - pass as object with reference key
            const response = await publicBookingAPI.verifyPayment({ reference });
            
            console.log('Verification response:', response.data);
            
            // Check for success - backend returns { success: true, message: "...", appointment_id: "..." }
            if (response.data.success || response.data.message === 'Payment verified') {
                setStatus('success');
                
                // Set appointment data from response
                setAppointment({
                    id: response.data.appointment_id,
                    confirmation_code: reference.slice(-8).toUpperCase(),
                    date: response.data.date || sessionStorage.getItem('booking_date'),
                    time: response.data.time || sessionStorage.getItem('booking_time'),
                    service_name: response.data.service_name || sessionStorage.getItem('booking_service'),
                    service_price: response.data.service_price || 0,
                    booking_fee_amount: response.data.booking_fee || 0,
                });
                
                // Load salon info for display
                if (salonId) {
                    try {
                        const salonRes = await publicBookingAPI.getSalon(salonId);
                        setSalonInfo(salonRes.data.salon || salonRes.data);
                    } catch (err) {
                        console.error('Failed to load salon info:', err);
                    }
                }
                
                // Clear session storage
                sessionStorage.removeItem('booking_reference');
                sessionStorage.removeItem('booking_salon_id');
                sessionStorage.removeItem('booking_date');
                sessionStorage.removeItem('booking_time');
                sessionStorage.removeItem('booking_service');
            } else {
                setStatus('failed');
                setError(getErrorMessage(response.data));
            }
        } catch (err) {
            console.error('Verification error:', err);
            setStatus('failed');
            setError(getErrorMessage(err));
        }
    };

    // Verifying state
    if (status === 'verifying') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <Card className="max-w-md w-full border-border/40 shadow-card">
                    <CardContent className="text-center py-16">
                        <Loader2 className="w-16 h-16 mx-auto mb-6 animate-spin text-primary" />
                        <h2 className="font-heading text-2xl font-bold mb-2">
                            Verifying Payment...
                        </h2>
                        <p className="text-muted-foreground">
                            Please wait while we confirm your payment
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                            Do not close this window
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Success state
    if (status === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <Card className="max-w-lg w-full border-border/40 shadow-card">
                    <CardContent className="text-center py-12">
                        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                            <Check className="w-10 h-10 text-green-600" />
                        </div>

                        <h2 className="font-heading text-3xl font-bold mb-2">
                            Booking Confirmed!
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Your payment has been confirmed and your appointment is booked.
                        </p>

                        {appointment && (
                            <div className="bg-secondary/50 rounded-xl p-6 text-left space-y-4 mb-6">
                                <div className="text-center pb-4 border-b">
                                    <p className="text-sm text-muted-foreground mb-2">Confirmation Code</p>
                                    <Badge variant="outline" className="font-mono text-2xl px-4 py-2">
                                        {appointment.confirmation_code || 'CONFIRMED'}
                                    </Badge>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Save this code for your records
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    {appointment.date && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <Calendar className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-muted-foreground">Date</p>
                                                <p className="font-semibold">{formatDate(appointment.date)}</p>
                                            </div>
                                        </div>
                                    )}

                                    {appointment.time && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <Clock className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-muted-foreground">Time</p>
                                                <p className="font-semibold">{formatTime(appointment.time)}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="pt-3 border-t space-y-2">
                                        {appointment.service_name && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Service</span>
                                                <span className="font-medium">{appointment.service_name}</span>
                                            </div>
                                        )}
                                        {appointment.service_price > 0 && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Service Price</span>
                                                <span className="font-semibold">{formatCurrency(appointment.service_price)}</span>
                                            </div>
                                        )}
                                        {appointment.booking_fee_amount > 0 && (
                                            <div className="flex justify-between text-green-700">
                                                <span className="flex items-center gap-1">
                                                    <Check className="w-4 h-4" />
                                                    Booking Fee Paid
                                                </span>
                                                <span className="font-semibold">{formatCurrency(appointment.booking_fee_amount)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <Alert className="mb-6">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="text-sm text-left">
                                A confirmation has been sent. Please save your confirmation code.
                            </AlertDescription>
                        </Alert>

                        {salonInfo?.phone && (
                            <div className="bg-muted/50 rounded-lg p-4 text-sm mb-6">
                                <p className="text-muted-foreground mb-2">Questions about your booking?</p>
                                <div className="flex items-center justify-center gap-2">
                                    <Phone className="w-4 h-4 text-primary" />
                                    <a href={`tel:${salonInfo.phone}`} className="font-medium text-primary hover:underline">
                                        {salonInfo.phone}
                                    </a>
                                </div>
                            </div>
                        )}

                        <Button size="lg" className="w-full rounded-full" onClick={() => window.location.href = '/'}>
                            Done
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Failed state
    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="max-w-md w-full border-border/40 shadow-card">
                <CardContent className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                        <X className="w-10 h-10 text-red-600" />
                    </div>

                    <h2 className="font-heading text-2xl font-bold mb-2">
                        Payment Failed
                    </h2>
                    <p className="text-muted-foreground mb-6">
                        {error || 'Your payment could not be processed.'}
                    </p>

                    <Alert variant="destructive" className="mb-6 text-left">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                            <p className="font-medium mb-1">What happened?</p>
                            <ul className="list-disc list-inside space-y-1 text-xs">
                                <li>Your appointment was not confirmed</li>
                                <li>If you were charged, the payment will be refunded within 5-7 business days</li>
                                <li>Please contact the salon if you need assistance</li>
                            </ul>
                        </AlertDescription>
                    </Alert>

                    {salonInfo?.phone && (
                        <div className="bg-muted/50 rounded-lg p-4 text-sm mb-6">
                            <p className="text-muted-foreground mb-2">Need help?</p>
                            <div className="flex items-center justify-center gap-2">
                                <Phone className="w-4 h-4 text-primary" />
                                <a href={`tel:${salonInfo.phone}`} className="font-medium text-primary hover:underline">
                                    Call {salonInfo.phone}
                                </a>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <Button
                            variant="default"
                            size="lg"
                            className="w-full rounded-full"
                            onClick={() => {
                                sessionStorage.clear();
                                window.history.back();
                            }}
                        >
                            Try Again
                        </Button>
                        <Button
                            variant="outline"
                            size="lg"
                            className="w-full rounded-full"
                            onClick={() => window.location.href = '/'}
                        >
                            Go Home
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}